import React, { useState } from 'react';
import { 
  AlertTriangle, 
  ExternalLink, 
  Sparkles, 
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Shield
} from 'lucide-react';
import '../styles/NatureAlerts.css';

const NatureAlerts = ({ alerts, aiSynthesis }) => {
  const [expandedAlerts, setExpandedAlerts] = useState(false);
  
  // Si no hi ha alertes ni síntesi, no mostrar res
  if ((!alerts || alerts.length === 0) && !aiSynthesis?.synthesis) return null;

  const getRiskClass = (level) => {
    const classes = {
      'baix': 'risk-low',
      'moderat': 'risk-moderate',
      'alt': 'risk-high',
      'crític': 'risk-critical',
      'critic': 'risk-critical'
    };
    return classes[level?.toLowerCase()] || 'risk-unknown';
  };

  const getRiskLabel = (level) => {
    const labels = {
      'baix': '✓ Risc Baix',
      'moderat': '⚠ Risc Moderat',
      'alt': '⚠ Risc Alt',
      'crític': '🚨 Risc Crític',
      'critic': '🚨 Risc Crític'
    };
    return labels[level?.toLowerCase()] || 'Risc Desconegut';
  };

  const getCategoryClass = (category) => {
    const classes = {
      'emergencia': 'category-emergency',
      'accessos': 'category-access',
      'meteo': 'category-weather',
      'fauna': 'category-fauna',
      'events': 'category-events',
      'mediambiental': 'category-environmental'
    };
    return classes[category] || '';
  };

  // Comptar alertes crítiques
  const criticalCount = alerts?.filter(a => 
    ['emergencia', 'accessos'].includes(a.category)
  ).length || 0;

  return (
    <section className="nature-alerts-section fade-in">
      <div className="alerts-container">
        <div className="alerts-header">
          <AlertTriangle className="alert-icon-main" size={24} />
          <div>
            <h3>Avisos de Medi Ambient i Natura</h3>
            <p className="alerts-subtitle">
              {aiSynthesis?.sources ? (
                <>📰 {aiSynthesis.sources.google_news || 0} + 🔵 {aiSynthesis.sources.gnews_api || 0} fonts analitzades</>
              ) : (
                'Notícies geolocalitzades i alertes oficials de la zona'
              )}
            </p>
          </div>
        </div>

        {/* Síntesi AI */}
        {aiSynthesis?.synthesis && (
          <div className="ai-synthesis-card">
            <div className="ai-synthesis-header">
              <div className="ai-badge">
                <Sparkles size={16} />
                <span>Anàlisi Intel·ligent</span>
              </div>
              <span className={`risk-badge ${getRiskClass(aiSynthesis.risk_level)}`}>
                {getRiskLabel(aiSynthesis.risk_level)}
              </span>
            </div>

            <p className="ai-synthesis-text">
              {aiSynthesis.synthesis}
            </p>

            {/* Consell principal */}
            {aiSynthesis.main_advice && (
              <div className="ai-main-advice">
                <Lightbulb size={16} className="advice-icon" />
                <span>{aiSynthesis.main_advice}</span>
              </div>
            )}

            {/* Recomanacions */}
            {aiSynthesis.recommendations?.length > 0 && (
              <div className="ai-recommendations">
                <p className="recommendations-title">Recomanacions:</p>
                <ul>
                  {aiSynthesis.recommendations.map((rec, idx) => (
                    <li key={idx}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Zones a evitar */}
            {aiSynthesis.zones_to_avoid?.length > 0 && (
              <div className="ai-zones-avoid">
                <span className="zones-icon">🚫</span>
                <div>
                  <strong>Zones a evitar:</strong>
                  <span>{aiSynthesis.zones_to_avoid.join(', ')}</span>
                </div>
              </div>
            )}

            {/* Parcs cercats */}
            {aiSynthesis.searched_parks?.length > 0 && (
              <div className="ai-parks-searched">
                🏞️ Parcs inclosos: {aiSynthesis.searched_parks.join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Botó per expandir/col·lapsar alertes */}
        {alerts && alerts.length > 0 && (
          <>
            <button 
              className="alerts-toggle-btn"
              onClick={() => setExpandedAlerts(!expandedAlerts)}
            >
              <span>
                {alerts.length} alertes trobades
                {criticalCount > 0 && (
                  <span className="critical-count">({criticalCount} crítiques)</span>
                )}
              </span>
              {expandedAlerts ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            {/* Llista d'alertes */}
            {expandedAlerts && (
              <div className="alerts-grid">
                {alerts.map((alert, index) => (
                  <div key={index} className={`alert-card ${getCategoryClass(alert.category)}`}>
                    <div className="alert-meta">
                      <div className="alert-meta-left">
                        <span className="alert-category-icon">{alert.icon}</span>
                        <span className="alert-category-label">{alert.category}</span>
                        <span className="alert-origin">
                          {alert.origin === 'google' ? '📰' : '🔵'}
                        </span>
                      </div>
                      <span className="alert-date">
                        {alert.published && new Date(alert.published).toLocaleDateString('ca-ES')}
                      </span>
                    </div>
                    <h4>{alert.title}</h4>
                    <p>{alert.summary}</p>
                    <div className="alert-footer">
                      <span className="alert-source">{alert.source}</span>
                      <a href={alert.url} target="_blank" rel="noopener noreferrer" className="alert-link">
                        Llegir més <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default NatureAlerts;